/**
 * PipelinesSettingsPage — manage fias_leads_pipelines + stages.
 * Create / rename pipelines, set default, add / edit / delete / reorder
 * stages.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsPipelines,
  useCreateFiasLeadsPipeline,
  useUpdateFiasLeadsPipeline,
  useDeleteFiasLeadsPipeline,
  useAddFiasLeadsStage,
  useUpdateFiasLeadsStage,
  useDeleteFiasLeadsStage,
} from '../hooks/useFiasLeads';
import type { FiasLeadsPipelineStageType } from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const STAGE_TYPE_COLORS: Record<FiasLeadsPipelineStageType, string> = {
  open: 'bg-blue-100 text-blue-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-rose-100 text-rose-700',
};

export function PipelinesSettingsPage({ holderId }: Props) {
  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const createPipeline = useCreateFiasLeadsPipeline(holderId);
  const updatePipeline = useUpdateFiasLeadsPipeline(holderId);
  const deletePipeline = useDeleteFiasLeadsPipeline(holderId);
  const addStage = useAddFiasLeadsStage(holderId);
  const updateStage = useUpdateFiasLeadsStage(holderId);
  const deleteStage = useDeleteFiasLeadsStage(holderId);

  const [newPipelineName, setNewPipelineName] = useState('');

  const handleCreatePipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPipelineName.trim()) return;
    await createPipeline.mutateAsync({ name: newPipelineName.trim() });
    setNewPipelineName('');
  };

  if (pipelinesQuery.isLoading) return <SkeletonBlock lines={6} />;

  const pipelines = pipelinesQuery.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <Link
        to={`${ARCHE_PATH}/settings`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Settings
      </Link>

      <header>
        <h1 className="text-2xl font-semibold">Pipelines</h1>
        <p className="text-sm text-muted-foreground">
          Sales pipelines and their stages. Holder-scoped.
        </p>
      </header>

      <form
        onSubmit={handleCreatePipeline}
        className="flex gap-2 rounded border border-border bg-card p-4"
      >
        <input
          type="text"
          placeholder="New pipeline name"
          value={newPipelineName}
          onChange={(e) => setNewPipelineName(e.target.value)}
          className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm"
          required
        />
        <button
          type="submit"
          disabled={!newPipelineName.trim() || createPipeline.isPending}
          className="rounded bg-primary px-4 py-2 text-primary-foreground text-sm"
        >
          {createPipeline.isPending ? 'Creating…' : 'Create pipeline'}
        </button>
      </form>

      {pipelines.length === 0 && <p className="text-sm text-muted-foreground">No pipelines yet.</p>}

      {pipelines.map((p) => (
        <div key={p.id} className="rounded border border-border bg-card p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium">{p.name}</h2>
              {p.is_default && (
                <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                  default
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {!p.is_default && (
                <button
                  onClick={() =>
                    updatePipeline.mutateAsync({
                      pipelineId: p.id,
                      input: { isDefault: true },
                    })
                  }
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Make default
                </button>
              )}
              <button
                onClick={() => {
                  if (
                    confirm(
                      `Delete pipeline "${p.name}"? Deals on this pipeline will be cascade-deleted.`,
                    )
                  ) {
                    deletePipeline.mutateAsync(p.id);
                  }
                }}
                className="text-xs text-muted-foreground hover:text-destructive"
              >
                Delete
              </button>
            </div>
          </div>

          <ul className="flex flex-col gap-1">
            {p.stages.map((s) => (
              <li key={s.id} className="flex items-center gap-2 text-sm">
                <span className={`rounded px-2 py-0.5 text-xs ${STAGE_TYPE_COLORS[s.stage_type]}`}>
                  {s.stage_type}
                </span>
                <input
                  type="text"
                  defaultValue={s.name}
                  onBlur={(e) => {
                    if (e.target.value !== s.name) {
                      updateStage.mutateAsync({
                        pipelineId: p.id,
                        stageId: s.id,
                        input: { name: e.target.value },
                      });
                    }
                  }}
                  className="flex-1 rounded border border-border bg-background px-2 py-1"
                />
                <select
                  defaultValue={s.stage_type}
                  onChange={(e) =>
                    updateStage.mutateAsync({
                      pipelineId: p.id,
                      stageId: s.id,
                      input: { stageType: e.target.value as FiasLeadsPipelineStageType },
                    })
                  }
                  className="rounded border border-border bg-background px-2 py-1 text-xs"
                >
                  <option value="open">open</option>
                  <option value="won">won</option>
                  <option value="lost">lost</option>
                </select>
                <button
                  onClick={() => {
                    if (confirm(`Delete stage "${s.name}"?`)) {
                      deleteStage.mutateAsync({ pipelineId: p.id, stageId: s.id });
                    }
                  }}
                  className="text-muted-foreground hover:text-destructive"
                  title="Delete stage"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <AddStageForm
            pipelineId={p.id}
            onAdd={(name, stageType) =>
              addStage.mutateAsync({
                pipelineId: p.id,
                input: { name, stageType },
              })
            }
          />
        </div>
      ))}
    </div>
  );
}

function AddStageForm({
  pipelineId: _pipelineId,
  onAdd,
}: {
  pipelineId: string;
  onAdd: (name: string, stageType: FiasLeadsPipelineStageType) => Promise<unknown>;
}) {
  const [name, setName] = useState('');
  const [stageType, setStageType] = useState<FiasLeadsPipelineStageType>('open');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onAdd(name.trim(), stageType);
    setName('');
    setStageType('open');
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2 mt-1">
      <Plus className="h-4 w-4 text-muted-foreground" />
      <input
        type="text"
        placeholder="New stage name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm"
      />
      <select
        value={stageType}
        onChange={(e) => setStageType(e.target.value as FiasLeadsPipelineStageType)}
        className="rounded border border-border bg-background px-2 py-1 text-xs"
      >
        <option value="open">open</option>
        <option value="won">won</option>
        <option value="lost">lost</option>
      </select>
      <button
        type="submit"
        disabled={!name.trim()}
        className="rounded bg-primary px-3 py-1 text-primary-foreground text-xs disabled:opacity-50"
      >
        Add
      </button>
    </form>
  );
}
